import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import type { ChannelType } from "@backstory/protocol";
import { BusyButton, ConfirmDialog, CopyButton, DataTable, Dialog, Field, PageHeader, RowMenu, Switch, useToast } from "../../components/ui";
import { EmptyPlugMark, EmptyState, ErrorState } from "../../components/States";
import { formatRelative } from "../../lib/format";
import { CHANNEL_TYPES, channelTypeLabel, notificationsApi, notifyKeys, WEBHOOK_DOCS_URL, type ChannelInput, type ChannelView, type TestResult } from "./api";
import { NotificationsTabs } from "./components";
import { useCan } from "../../auth/roles";

type Wizard = { step: "type" | "config"; id?: string; type: ChannelType; name: string; config: Record<string, string>; secrets: Record<string, string>; secretsSet: string[] };

const emptyWizard = (): Wizard => ({ step: "type", type: "slack", name: "", config: {}, secrets: {}, secretsSet: [] });

function azureOrgName(raw: string): string {
  const s = raw.trim();
  if (!s) return "";
  const ok = (org: string) => /^[A-Za-z0-9][A-Za-z0-9-]{0,62}$/.test(org);
  if (s.includes("://") || s.includes(".")) {
    try {
      const u = new URL(s);
      const host = u.hostname.toLowerCase();
      if (host === "dev.azure.com") {
        const org = u.pathname.split("/").filter(Boolean)[0] ?? "";
        return ok(org) ? org : "";
      }
      if (host.endsWith(".visualstudio.com")) {
        const org = host.slice(0, -".visualstudio.com".length);
        return ok(org) ? org : "";
      }
      return "";
    } catch {
      return "";
    }
  }
  return ok(s) ? s : "";
}

function kept(w: Wizard, key: string): boolean {
  return !!w.secrets[key]?.trim() || (!!w.id && w.secretsSet.includes(key));
}

function wizardFromChannel(c: ChannelView): Wizard {
  const config: Record<string, string> = {};
  for (const [k, v] of Object.entries(c.config ?? {})) {
    if (k.endsWith("Set")) continue;
    if (typeof v === "string") config[k] = v;
    else if (Array.isArray(v) && v.every((x) => typeof x === "string")) config[k] = v.join(", ");
    else if (k === "headers" && v && typeof v === "object") config[k] = Object.entries(v as Record<string, string>).map(([hk, hv]) => `${hk}: ${hv}`).join("\n");
  }
  return { step: "config", id: c.id, type: c.type, name: c.name, config, secrets: {}, secretsSet: c.secretsSet ?? [] };
}

export function validateChannel(w: Wizard): Record<string, string> {
  const e: Record<string, string> = {};
  if (!w.name.trim()) e.name = "Name the channel.";
  const c = w.config;
  switch (w.type) {
    case "email":
      if (c.to && c.to.split(",").some((a) => a.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(a.trim()))) e.to = "One or more addresses look invalid.";
      break;
    case "slack":
      if (!c.channelId) e.channelId = "Pick a Slack channel.";
      break;
    case "teams":
      if (!kept(w, "webhookUrl") || (w.secrets.webhookUrl && !/^https:\/\//.test(w.secrets.webhookUrl))) e.webhookUrl = "Paste the Incoming Webhook URL (https).";
      break;
    case "webhook":
      if (!/^https?:\/\/.+/.test(c.url ?? "")) e.url = "Enter an http(s) URL.";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((c.alertEmail ?? "").trim())) e.alertEmail = "Enter the address that should hear if this webhook is disabled.";
      break;
    case "jira":
      if (!/^https:\/\//.test(c.baseUrl ?? "")) e.baseUrl = "Site URL, e.g. https://acme.atlassian.net";
      if (!c.projectKey) e.projectKey = "Project key is required.";
      if (!c.email) e.email = "Account email is required.";
      if (!kept(w, "apiToken")) e.apiToken = "API token is required.";
      break;
    case "github":
      if (!/^[^/\s]+\/[^/\s]+$/.test(c.repo ?? "")) e.repo = "Use owner/name.";
      if (!kept(w, "token")) e.token = "Token is required.";
      break;
    case "azuredevops": {
      if (!azureOrgName(c.organization ?? "")) e.organization = "Enter the organization name, or https://dev.azure.com/your-org.";
      if (!c.project?.trim() || /[/\\?#]/.test(c.project)) e.project = "Project name is required.";
      const workItemType = c.workItemType ?? "";
      if (workItemType.trim() && /[/\\$]/.test(workItemType)) e.workItemType = "Use the type name, such as Issue or Bug.";
      if (!kept(w, "pat")) e.pat = "Personal access token is required.";
      break;
    }
    case "pagerduty":
      if (!kept(w, "routingKey")) e.routingKey = "Routing key is required.";
      break;
    case "opsgenie":
      if (!kept(w, "apiKey")) e.apiKey = "API key is required.";
      break;
    case "linear":
      if (!c.teamId?.trim()) e.teamId = "Team is required.";
      if (!kept(w, "apiKey")) e.apiKey = "API key is required.";
      break;
    default:
      break;
  }
  return e;
}

export function toChannelInput(w: Wizard): ChannelInput {
  const config: Record<string, unknown> = { ...w.config };
  if (w.type === "email") config.to = w.config.to ? w.config.to.split(",").map((s) => s.trim()).filter(Boolean) : [];
  if (w.type === "azuredevops") {
    config.organization = azureOrgName(w.config.organization ?? "");
    config.project = (w.config.project ?? "").trim();
    config.workItemType = (w.config.workItemType ?? "").trim() || "Issue";
  }
  if (w.type === "linear") config.teamId = (w.config.teamId ?? "").trim();
  if (w.type === "webhook") {
    config.alertEmail = (w.config.alertEmail ?? "").trim();
    const h: Record<string, string> = {};
    for (const line of (w.config.headers ?? "").split("\n")) {
      const [k, ...rest] = line.split(":");
      if (k?.trim() && rest.length) h[k.trim()] = rest.join(":").trim();
    }
    config.headers = h;
  }
  const secrets = Object.fromEntries(Object.entries(w.secrets).filter(([, v]) => v));
  return { type: w.type, name: w.name.trim(), config, secrets: Object.keys(secrets).length ? secrets : undefined };
}

function normURL(raw: string): string {
  const t = raw.trim();
  try {
    const u = new URL(t);
    u.protocol = u.protocol.toLowerCase();
    u.hostname = u.hostname.toLowerCase();
    u.pathname = u.pathname.replace(/\/+$/, "");
    return u.toString();
  } catch {
    return t.toLowerCase().replace(/\/+$/, "");
  }
}

function emailSet(raw: string): string {
  return [...new Set(raw.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean))].sort().join(",");
}

/** Same key the API uses: identical keys send to the same place. Teams URLs are secrets, so the API checks those. */
function destinationKey(type: ChannelType, config: Record<string, string>): string {
  switch (type) {
    case "inbox":
      return "inbox";
    case "email":
      return "email:" + emailSet(config.to ?? "");
    case "webhook":
      return config.url?.trim() ? "webhook:" + normURL(config.url) : "";
    case "slack":
      return config.channelId?.trim() ? "slack:" + config.channelId.trim() : "";
    case "jira": {
      if (!config.baseUrl?.trim() || !config.projectKey?.trim()) return "";
      const issue = config.issueType?.trim().toLowerCase() || "bug";
      return "jira:" + normURL(config.baseUrl) + "|" + config.projectKey.trim().toUpperCase() + "|" + issue;
    }
    case "github":
      return config.repo?.trim() ? "github:" + config.repo.trim().toLowerCase() : "";
    case "azuredevops": {
      const org = azureOrgName(config.organization ?? "");
      if (!org || !config.project?.trim()) return "";
      const wit = config.workItemType?.trim().toLowerCase() || "issue";
      return "azuredevops:" + org.toLowerCase() + "|" + config.project.trim().toLowerCase() + "|" + wit;
    }
    case "linear":
      return config.teamId?.trim() ? "linear:" + config.teamId.trim() : "";
    default:
      return "";
  }
}

function channelDestination(c: ChannelView): string {
  const cfg: Record<string, string> = {};
  for (const [k, v] of Object.entries(c.config ?? {})) {
    if (typeof v === "string") cfg[k] = v;
    else if (Array.isArray(v) && v.every((x) => typeof x === "string")) cfg[k] = (v as string[]).join(",");
  }
  return destinationKey(c.type, cfg);
}

const duplicateField: Partial<Record<ChannelType, string>> = {
  email: "to",
  webhook: "url",
  slack: "channelId",
  jira: "projectKey",
  github: "repo",
  azuredevops: "project",
  linear: "teamId",
  inbox: "name",
};

export function duplicateError(w: Wizard, existing: ChannelView[]): string | null {
  const key = destinationKey(w.type, w.config);
  if (!key) return null;
  const hit = existing.find((c) => c.id !== w.id && channelDestination(c) === key);
  if (!hit) return null;
  if (w.type === "inbox") return "An in-app inbox is already connected.";
  return `${hit.name} already sends to this destination.`;
}

export function ChannelsPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const search = useSearch({ strict: false }) as { slack?: string; team?: string; focus?: string };
  const qc = useQueryClient();
  const toast = useToast();
  const channels = useQuery({ queryKey: notifyKeys.channels(orgId), queryFn: () => notificationsApi.channels(orgId) });
  const slackInstall = useQuery({ queryKey: ["notify", "slack-install", orgId], queryFn: () => notificationsApi.slackInstallUrl(orgId) });
  const can = useCan();
  const [wizard, setWizard] = useState<Wizard | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [revealed, setRevealed] = useState<{ name: string; secret: string } | null>(null);
  const [removing, setRemoving] = useState<ChannelView | null>(null);
  const [testResult, setTestResult] = useState<Record<string, TestResult>>({});
  const invalidate = () => qc.invalidateQueries({ queryKey: notifyKeys.channels(orgId) });

  useEffect(() => {
    if (search.slack === "installed") toast.push("info", `Slack workspace ${search.team ?? ""} connected. Now add a Slack channel.`);
    if (search.slack === "denied" || search.slack === "error") toast.push("error", "Slack installation did not complete.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.slack]);

  useEffect(() => {
    if (!search.focus || !channels.data?.some((c) => c.id === search.focus)) return;
    document.querySelector(`[data-row-id="${CSS.escape(search.focus)}"]`)?.scrollIntoView({ block: "center" });
  }, [search.focus, channels.data]);

  const create = useMutation({
    mutationFn: (w: Wizard) => notificationsApi.createChannel(orgId, toChannelInput(w)),
    onSuccess: (res) => {
      setWizard(null);
      void invalidate();
      if (res.revealedSecrets?.secret) setRevealed({ name: res.channel.name, secret: res.revealedSecrets.secret });
      toast.push("info", "Channel created.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const update = useMutation({
    mutationFn: (w: Wizard) => notificationsApi.patchChannel(orgId, w.id!, toChannelInput(w)),
    onSuccess: () => {
      setWizard(null);
      void invalidate();
      toast.push("info", "Channel updated.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => notificationsApi.patchChannel(orgId, id, { enabled }),
    onSuccess: () => void invalidate(),
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const del = useMutation({
    mutationFn: (id: string) => notificationsApi.deleteChannel(orgId, id),
    onSuccess: () => {
      setRemoving(null);
      void invalidate();
      void qc.invalidateQueries({ queryKey: notifyKeys.rules(orgId) });
      toast.push("info", "Channel removed.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const test = useMutation({
    mutationFn: (id: string) => notificationsApi.testChannel(orgId, id),
    onSuccess: (r, id) => {
      setTestResult((m) => ({ ...m, [id]: r }));
      const ch = channels.data?.find((c) => c.id === id);
      if (r.ok && ch?.type === "webhook" && !ch.enabled) {
        void qc.invalidateQueries({ queryKey: notifyKeys.inbox(orgId) });
        toggle.mutate({ id, enabled: true }, { onSuccess: () => toast.push("info", "Test succeeded. Webhook is back on.") });
        return;
      }
      toast.push(r.ok ? "info" : "error", r.ok ? "Test message sent." : `Test failed: ${r.error ?? r.responseCode}`);
      void invalidate();
      if (r.ok) void qc.invalidateQueries({ queryKey: notifyKeys.inbox(orgId) });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const rotate = useMutation({
    mutationFn: (id: string) => notificationsApi.rotateSecret(orgId, id),
    onSuccess: (r, id) => {
      const ch = channels.data?.find((c) => c.id === id);
      setRevealed({ name: ch?.name ?? "webhook", secret: r.secret });
      toast.push("info", "Secret rotated. The previous secret keeps verifying for 24 hours.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const openWizard = () => { setErrors({}); setWizard(emptyWizard()); };

  return (
    <>
      <PageHeader
        title="Notification channels"
        actions={
          can.develop && (
            <>
              {slackInstall.data?.configured && (
                <a className="btn" href={slackInstall.data.url}>
                  Add to Slack
                </a>
              )}
              <button className="btn primary" onClick={openWizard}>Add channel</button>
            </>
          )
        }
      >
        <NotificationsTabs orgId={orgId} />
      </PageHeader>
      <div className="card">
        {channels.isError ? (
          <ErrorState error={channels.error} retry={() => void channels.refetch()} />
        ) : channels.isSuccess && channels.data.length === 0 ? (
          <EmptyState mark={<EmptyPlugMark />} title="No channels yet" action={can.develop && <button className="btn primary" onClick={openWizard}>Add channel</button>} />
        ) : (
        <div className="channels-table">
        <DataTable
          ariaLabel="Channels"
          rows={channels.data ?? []}
          loading={channels.isPending}
          rowKey={(c) => c.id}
          rowClassName={(c) => (c.id === search.focus ? "row-focus" : undefined)}
          columns={[
            { key: "on", header: "On", render: (c) => {
              const waking = c.type === "webhook" && !c.enabled;
              const testing = (test.isPending && test.variables === c.id) || (toggle.isPending && toggle.variables?.id === c.id);
              return (
                <Switch
                  checked={c.enabled}
                  disabled={testing}
                  title={waking ? "A successful test turns this back on." : undefined}
                  onChange={(v) => { if (waking && v) { test.mutate(c.id); return; } toggle.mutate({ id: c.id, enabled: v }); }}
                  label={<span className="sr-only">{waking ? `Test and enable ${c.name}` : `Enable ${c.name}`}</span>}
                />
              );
            } },
            { key: "name", header: "Channel", render: (c) => (
              <>
                <strong>{c.name}</strong>
                <div className="muted small">{channelTypeLabel(c.type)} · <ConfigSummary c={c} /></div>
                {c.type === "webhook" && !c.enabled && <div className="muted small">{test.isPending && test.variables === c.id ? "Testing…" : "A successful test turns this back on."}</div>}
              </>
            ) },
            {
              key: "health",
              header: "Last delivery",
              label: "Last delivery",
              render: (c) => (
                <span className="small">
                  {c.lastDeliveryAt ? formatRelative(c.lastDeliveryAt) : <span className="muted">never</span>}
                  {c.lastError && <div className="danger small" title={c.lastError}>error: {c.lastError.slice(0, 60)}</div>}
                  {testResult[c.id] && <div className={`${testResult[c.id]!.ok ? "muted" : "danger"} small`}>test: {testResult[c.id]!.ok ? `ok (${testResult[c.id]!.responseCode})` : testResult[c.id]!.error}</div>}
                </span>
              ),
            },
            {
              key: "act",
              header: "",
              align: "right",
              render: (c) => (
                <RowMenu label={`Actions for ${c.name}`} testId={`channel-menu-${c.id}`}>
                  <button role="menuitem" onClick={() => test.mutate(c.id)} disabled={test.isPending}>Send test</button>
                  {can.develop && <button role="menuitem" onClick={() => { setErrors({}); setWizard(wizardFromChannel(c)); }}>Edit</button>}
                  {can.develop && c.type === "webhook" && <button role="menuitem" onClick={() => rotate.mutate(c.id)}>Rotate secret</button>}
                  <Link role="menuitem" to={"/orgs/$org/notifications/deliveries" as never} params={{ org: orgId } as never} search={{ channelId: c.id } as never}>Delivery log</Link>
                  {can.develop && <button role="menuitem" className="danger" onClick={() => setRemoving(c)}>Remove</button>}
                </RowMenu>
              ),
            },
          ]}
        />
        </div>
        )}
      </div>

      <ConfirmDialog
        open={!!removing}
        danger
        title="Remove channel"
        confirmLabel="Remove channel"
        busy={del.isPending}
        onClose={() => setRemoving(null)}
        onConfirm={() => removing && del.mutate(removing.id)}
      >
        <p style={{ margin: 0 }}>
          <strong>{removing?.name}</strong> is removed from every rule that sends to it. A rule left with no channel is paused.
        </p>
      </ConfirmDialog>

      {wizard && (
        <ChannelWizard
          wizard={wizard}
          errors={errors}
          orgId={orgId}
          slackConfigured={!!slackInstall.data?.configured}
          slackInstallUrl={slackInstall.data?.url ?? ""}
          onChange={(next) => { setWizard(next); setErrors({}); }}
          onClose={() => setWizard(null)}
          saving={create.isPending || update.isPending}
          channels={channels.data ?? []}
          onSave={() => {
            const e = validateChannel(wizard);
            const clash = duplicateError(wizard, channels.data ?? []);
            if (clash) e[duplicateField[wizard.type] ?? "name"] = clash;
            setErrors(e);
            if (Object.keys(e).length > 0) return;
            if (wizard.id) update.mutate(wizard);
            else create.mutate(wizard);
          }}
        />
      )}

      <Dialog open={!!revealed} onClose={() => setRevealed(null)} title="Webhook signing secret" footer={<button className="btn primary" onClick={() => setRevealed(null)}>I have saved it</button>}>
        {revealed && (
          <>
            <p>
              This is the only time the secret for <strong>{revealed.name}</strong> is shown. Store it in your secret manager and use it to verify the <code>X-Backstory-Signature</code> header.
            </p>
            <div className="secret-box">
              <code className="mono">{revealed.secret}</code>
              <CopyButton value={revealed.secret} />
            </div>
            <p className="small muted secret-note" style={{ marginTop: 12, marginBottom: 0 }}>
              Verification examples for Node.js, Python, Go, .NET, Java, Ruby, and PHP are in the <a href={WEBHOOK_DOCS_URL} target="_blank" rel="noreferrer">webhook guide</a>.
            </p>
          </>
        )}
      </Dialog>
    </>
  );
}

function ConfigSummary({ c }: { c: ChannelView }) {
  const cfg = c.config as Record<string, unknown>;
  switch (c.type) {
    case "email":
      return <>{Array.isArray(cfg.to) && cfg.to.length ? (cfg.to as string[]).join(", ") : "owners and admins"}</>;
    case "slack":
      return <>#{(cfg.channelName as string) ?? (cfg.channelId as string) ?? "?"}</>;
    case "teams":
      return <>Incoming Webhook</>;
    case "webhook":
      return <><span className="mono">{cfg.url as string}</span>{typeof cfg.alertEmail === "string" && cfg.alertEmail ? <> · {cfg.alertEmail}</> : null}</>;
    case "jira":
      return <>{cfg.baseUrl as string} · {cfg.projectKey as string}</>;
    case "github":
      return <>{cfg.repo as string}</>;
    case "azuredevops":
      return <>{cfg.organization as string} / {cfg.project as string}</>;
    case "pagerduty":
      return <>On-call incident</>;
    case "opsgenie":
      return <>On-call alert</>;
    case "linear":
      return <span className="mono">{cfg.teamId as string}</span>;
    case "inbox":
      return <>all members</>;
    default:
      return <>{c.secretsSet.length ? "credentials set" : "no credentials"}</>;
  }
}

function ChannelWizard({ wizard, errors, channels, orgId, slackConfigured, slackInstallUrl, onChange, onClose, onSave, saving }: {
  wizard: Wizard;
  errors: Record<string, string>;
  channels: ChannelView[];
  orgId: string;
  slackConfigured: boolean;
  slackInstallUrl: string;
  onChange: (w: Wizard) => void;
  onClose: () => void;
  onSave: () => void;
  saving: boolean;
}) {
  const setCfg = (k: string, v: string) => onChange({ ...wizard, config: { ...wizard.config, [k]: v } });
  const setSecret = (k: string, v: string) => onChange({ ...wizard, secrets: { ...wizard.secrets, [k]: v } });
  const meta = CHANNEL_TYPES.find((t) => t.id === wizard.type) ?? { id: wizard.type, label: channelTypeLabel(wizard.type), description: "" };
  const keepHint = (key: string, hint: string) => (wizard.id && wizard.secretsSet.includes(key) ? `${hint} Leave blank to keep the saved value.`.trim() : hint);
  const [slackChannels, setSlackChannels] = useState<Array<{ id: string; name: string }> | null>(null);
  const [slackErr, setSlackErr] = useState<string | null>(null);

  useEffect(() => {
    if (wizard.step === "config" && wizard.type === "slack" && slackConfigured && slackChannels === null) {
      notificationsApi.slackOrgConversations(orgId).then((r) => setSlackChannels(r.channels)).catch((e) => setSlackErr((e as Error).message));
    }
  }, [wizard.step, wizard.type, slackConfigured, slackChannels, orgId]);

  const problems = validateChannel(wizard);
  const fieldError = (k: string) => {
    if (errors[k]) return errors[k];
    const value = (k === "name" ? wizard.name : wizard.secrets[k] || wizard.config[k] || "").trim();
    return value ? problems[k] : undefined;
  };
  const ready = Object.keys(problems).length === 0;
  const text = (k: string, label: string, opts: { hint?: string; placeholder?: string; secret?: boolean; textarea?: boolean } = {}) => (
    <Field key={k} label={label} hint={opts.hint} error={fieldError(k)}>
      {(p) =>
        opts.textarea ? (
          <textarea {...p} className="input" rows={3} value={(opts.secret ? wizard.secrets[k] : wizard.config[k]) ?? ""} onChange={(e) => (opts.secret ? setSecret(k, e.target.value) : setCfg(k, e.target.value))} placeholder={opts.placeholder} />
        ) : (
          <input {...p} className="input" type={opts.secret ? "password" : "text"} autoComplete="off" value={(opts.secret ? wizard.secrets[k] : wizard.config[k]) ?? ""} onChange={(e) => (opts.secret ? setSecret(k, e.target.value) : setCfg(k, e.target.value))} placeholder={opts.placeholder} />
        )
      }
    </Field>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={wizard.id ? `Edit ${meta.label}` : wizard.step === "type" ? "Add channel" : `Add ${meta.label}`}
      width={640}
      footer={
        wizard.step === "type" ? (
          <>
            <button className="btn" onClick={onClose}>Cancel</button>
            <button className="btn primary" onClick={() => onChange({ ...wizard, step: "config", name: wizard.name && !CHANNEL_TYPES.some((t) => t.label === wizard.name) ? wizard.name : meta.label })}>Continue</button>
          </>
        ) : (
          <>
            {wizard.id ? <button className="btn" onClick={onClose} disabled={saving}>Cancel</button> : <button className="btn" onClick={() => onChange({ ...wizard, step: "type" })} disabled={saving}>Back</button>}
            <BusyButton className="btn primary" onClick={onSave} busy={saving} busyLabel="Saving…" disabled={!ready}>{wizard.id ? "Save changes" : "Create channel"}</BusyButton>
          </>
        )
      }
    >
      {wizard.step === "type" ? (
        <div className="type-grid" role="radiogroup" aria-label="Channel type">
          {CHANNEL_TYPES.map((t) => {
            const taken = t.id === "inbox" && channels.some((c) => c.type === "inbox");
            return (
              <label key={t.id} className={`type-card ${wizard.type === t.id ? "selected" : ""} ${taken ? "disabled" : ""}`}>
                <input type="radio" name="channel-type" value={t.id} checked={wizard.type === t.id} disabled={taken} onChange={() => onChange({ ...wizard, type: t.id, config: {}, secrets: {} })} autoComplete="off" />
                <strong>{t.label}</strong>
                <span className="muted small">{taken ? "Already connected" : t.description}</span>
              </label>
            );
          })}
        </div>
      ) : (
        <div className="form-grid">
          <Field label="Name" error={fieldError("name")}>{(p) => <input {...p} className="input" value={wizard.name} onChange={(e) => onChange({ ...wizard, name: e.target.value })} autoFocus />}</Field>
          {wizard.type === "email" && text("to", "Recipients", { hint: "Comma-separated. Leave empty to email all owners and admins.", placeholder: "oncall@acme.com, eng-leads@acme.com" })}
          {wizard.type === "slack" && (
            <>
              {!slackConfigured && <div className="card warn small">Slack is not configured on this server (BACKSTORY_SLACK_CLIENT_ID). Self-hosted installs can paste a bot token instead.</div>}
              {slackConfigured && (
                <div className="small">
                  Workspace not connected yet? <a href={slackInstallUrl}>Add to Slack</a> first.
                </div>
              )}
              <Field label="Slack channel" error={fieldError("channelId")} hint={slackErr ?? "Pick a public or private channel the Backstory bot can post to."}>
                {(p) =>
                  slackChannels && slackChannels.length ? (
                    <select {...p} className="input" value={wizard.config.channelId ?? ""} onChange={(e) => { const c = slackChannels.find((x) => x.id === e.target.value); onChange({ ...wizard, config: { ...wizard.config, channelId: e.target.value, channelName: c?.name ?? "" } }); }}>
                      <option value="">Select…</option>
                      {slackChannels.map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
                    </select>
                  ) : (
                    <input {...p} className="input" placeholder="C0123456789" value={wizard.config.channelId ?? ""} onChange={(e) => setCfg("channelId", e.target.value)} />
                  )
                }
              </Field>
              {text("botToken", "Bot token (optional)", { hint: keepHint("botToken", "Only for self-hosted setups without the Slack app install flow."), secret: true, placeholder: "xoxb-…" })}
            </>
          )}
          {wizard.type === "teams" && text("webhookUrl", "Incoming Webhook URL", { hint: keepHint("webhookUrl", "In the channel, open Connectors or Workflows and create an Incoming Webhook. The URL is stored encrypted."), secret: true, placeholder: "https://acme.webhook.office.com/…" })}
          {wizard.type === "webhook" && (
            <>
              {text("url", "Endpoint URL", { placeholder: "https://hooks.acme.com/backstory" })}
              {text("alertEmail", "Disable notice email", { hint: "We email this address if the webhook is turned off after repeated failures.", placeholder: "ops@acme.com" })}
              {text("headers", "Extra headers", { hint: "One per line, Header: value. Authorization is ignored unless allowAuthHeader=true.", textarea: true })}
              {!wizard.id && <div className="card small">A signing secret is generated when you create the channel and shown once. Every request carries <code>X-Backstory-Signature: t=…,v1=…</code>.</div>}
              {wizard.id && <div className="card small">The signing secret stays in place. Use Rotate secret on the channel row to replace it.</div>}
            </>
          )}
          {wizard.type === "jira" && (
            <>
              {text("baseUrl", "Site URL", { placeholder: "https://acme.atlassian.net" })}
              {text("projectKey", "Project key", { placeholder: "ENG" })}
              {text("issueType", "Issue type", { placeholder: "Bug" })}
              {text("email", "Account email")}
              {text("apiToken", "API token", { hint: keepHint("apiToken", "Stored encrypted."), secret: true })}
            </>
          )}
          {wizard.type === "github" && (
            <>
              {text("repo", "Repository", { placeholder: "acme/web-app" })}
              {text("token", "Token", { hint: keepHint("token", "Fine-grained token with Issues: read and write. Stored encrypted."), secret: true })}
            </>
          )}
          {wizard.type === "azuredevops" && (
            <>
              {text("organization", "Organization", { hint: "The name from dev.azure.com, or the full https://dev.azure.com/your-org URL.", placeholder: "acme" })}
              {text("project", "Project", { placeholder: "Web App" })}
              {text("workItemType", "Work item type", { hint: "Must exist in the project process. Leave blank to use Issue.", placeholder: "Issue" })}
              {text("pat", "Personal access token", { hint: keepHint("pat", "Work Items: read and write. Stored encrypted."), secret: true })}
            </>
          )}
          {wizard.type === "pagerduty" && text("routingKey", "Routing key", { hint: keepHint("routingKey", "From the PagerDuty service integration. Stored encrypted."), secret: true })}
          {wizard.type === "opsgenie" && text("apiKey", "API key", { hint: keepHint("apiKey", "From the Opsgenie integration. Stored encrypted."), secret: true })}
          {wizard.type === "linear" && (
            <>
              {text("teamId", "Team", { hint: "The team identifier from Linear settings.", placeholder: "team_…" })}
              {text("apiKey", "API key", { hint: keepHint("apiKey", "A personal key that can create issues. Stored encrypted."), secret: true })}
            </>
          )}
          {wizard.type === "inbox" && <div className="small muted">Every member of the organization sees these notifications in their inbox.</div>}
        </div>
      )}
    </Dialog>
  );
}
